import type { CSSInterpolation } from '@emotion/serialize';
import _ from 'lodash';
import type { ImgHTMLAttributes } from 'react';
import { useState } from 'react';

import IconPictureLine from '~icons/clarity/picture-line';

import { theme } from '@adflow/frontend/ui-theme';
import type { AssignableRef } from '@adflow/frontend/util-common';
import { useOnChange } from '@adflow/frontend/util-common';

import { useCallout } from './Callout';

/**
 * Globally keep track of which image URLs have failed.
 */
const failedUrls = new Set<string>();

function FallbackImage(props: {
	width: number | 'auto';
	height: number | 'auto';
}) {
	const { width, height } = props;
	return (
		<IconPictureLine
			width={width}
			height={height}
			css={{ color: theme.colors.action.disabledBackground }}
		/>
	);
}

export interface RenderImageProps extends ImgHTMLAttributes<HTMLImageElement> {
	alt: string;
	src: string | undefined;
	imageStyle?: CSSInterpolation;
	width: number | 'auto';
	height: number | 'auto';
	fallback?: string;
	hover?: boolean;
	imgRef?: AssignableRef<HTMLImageElement | null>;
}

/**
 * A component that renders a image or a broken image placeholder if an error is encountered
 */
export function RenderImage({
	alt,
	src,
	imageStyle,
	width,
	height,
	fallback,
	hover,
	imgRef,
	...imageProps
}: RenderImageProps) {
	const [showFallback, setShowFallback] = useState(!src || failedUrls.has(src));
	const [imageError, setImageError] = useState(
		(!src || failedUrls.has(src)) && (!fallback || failedUrls.has(fallback))
	);

	const debouncedHandleMouseEnter = _.debounce(
		(e) => (callout ? null : setShowViewCallout(e)),
		500
	);

	const { callout, setShowViewCallout } = useCallout({
		text: imageError ? (
			<FallbackImage width={width} height={height} />
		) : (
			<img
				alt={alt}
				src={showFallback ? fallback : src}
				css={{ maxHeight: '70vh' }}
			/>
		),
		maxWidth: '70vw',
		horizontal: 'right',
		tooltip: false,
	});

	useOnChange(
		src,
		() => {
			setImageError(!src || failedUrls.has(src));
		},
		_.isEqual
	);

	if (imageError) {
		return <FallbackImage width="auto" height="auto" />;
	}

	return (
		<>
			{imageError ? (
				<FallbackImage width={width} height={height} />
			) : (
				<img
					ref={imgRef}
					alt={alt}
					src={showFallback ? fallback : src}
					css={[
						imageStyle,
						{
							maxWidth: typeof width === 'number' ? width : undefined,
							maxHeight: typeof height === 'number' ? height : undefined,
						},
					]}
					onClick={(e) => (callout ? null : setShowViewCallout(e))}
					onMouseEnter={debouncedHandleMouseEnter}
					onMouseLeave={() => debouncedHandleMouseEnter.cancel()}
					{...imageProps}
					onError={() => {
						/**
						 * Mark the src as failed so that it goes straight to
						 * the fallback on remounting.
						 */
						if (src) {
							failedUrls.add(src);
						}

						// if src and fallback are the same, React will not detect a change,
						// which prevents us from ever getting back into this callback
						// e.g. src fails, fallback fails, then we get a broken image because
						// imageError is never set to true
						if (fallback && !showFallback && src !== fallback) {
							setShowFallback(true);
						} else {
							if (fallback) {
								failedUrls.add(fallback);
							}
							setImageError(true);
						}
					}}
				/>
			)}
			{hover ? callout : null}
		</>
	);
}
